import React from "react";
import { Button, Spin } from "antd";
import { ReloadOutlined, ThunderboltOutlined } from "@ant-design/icons";
import InsufficientDataState from "components/WelcomePage/InsufficientDataState";
import { resolveWelcomeWidgetMinReviewCount } from "constants/welcomeWidgetAnalysis";
import { resolveWelcomeReviewScopeFromData } from "pages/Welcome/welcomeReviewScopeLabel";
import { WelcomeWidgetJobProgressView } from "pages/Welcome/welcomeWidgetJobTiming";

/** Matches pivony-api welcome.py WELCOME_WIDGET_DATA_STATUS_* constants. */
export const WELCOME_WIDGET_DATA_STATUS_EMPTY = "empty";
export const WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT = "insufficient";
export const WELCOME_WIDGET_DATA_STATUS_FAILED = "failed";
export const WELCOME_WIDGET_DATA_STATUS_PENDING = "pending";

/** Brand GenAI metrics whose body is produced by the DeepSeek worker (20 table, 21/23/25 narrative). */
export const WELCOME_GENAI_CONTENT_METRIC_IDS = new Set([20, 21, 23, 25]);

export function isWelcomeGenAiContentMetricWidget(widget) {
  const mid = Number(widget?.metric_id);
  if (!WELCOME_GENAI_CONTENT_METRIC_IDS.has(mid)) return false;
  const mt = widget?.metricType;
  return mt === "brand" || mt == null || mt === undefined;
}

/** GenAI metrics (20/21/23/25) that consume AI credits on regenerate. */
export function isWelcomeGenAiRegenerableWidget(widget) {
  return isWelcomeGenAiContentMetricWidget(widget);
}

/** KPI snapshot refresh (charts, brand KPI hero, hot terms, etc.) — no LLM job, no credit modal. */
export function isWelcomeKpiOnlyRegenerateWidget(widget) {
  const mid = Number(widget?.metric_id);
  if (!Number.isFinite(mid) || mid <= 0) return false;
  if (widget?.metricType != null && widget.metricType !== "brand") return false;
  if (isWelcomeGenAiRegenerableWidget(widget)) return false;
  if (mid === 27) return false;
  return true;
}

/** Non-GenAI regenerate: call genai-requeue directly without subscriptions quota check. */
export function welcomeRegenerateSkipsQuotaCheck(widget) {
  return isWelcomeKpiOnlyRegenerateWidget(widget);
}

/**
 * User-facing welcomeWidgetJob failure text without vendor/provider names.
 * Returns null when the raw API message should be replaced by a translated fallback.
 */
export function resolveWelcomeJobStatusDescription(raw, t) {
  const s = String(raw || "").trim();
  if (!s) return null;
  if (
    /deepseek/i.test(s) ||
    s === "AI service is temporarily unavailable."
  ) {
    return t("welcome.widgetGenaiServiceUnavailable");
  }
  return s;
}

/** True when genai-requeue should not show the credit quota modal or charge upfront.
 * Only regenerating a previously completed widget consumes credits on requeue.
 */
export function welcomeGenaiRegenerateSkipsCreditCharge(rawStatus) {
  if (rawStatus === 3 || rawStatus === "completed" || rawStatus === "ready") {
    return false;
  }
  if (
    rawStatus == null ||
    rawStatus === 1 ||
    rawStatus === 2 ||
    rawStatus === 4 ||
    rawStatus === "pending" ||
    rawStatus === "in_progress" ||
    rawStatus === "failed" ||
    rawStatus === "loading" ||
    rawStatus === "error"
  ) {
    return true;
  }
  return true;
}

/** True when welcomeWidgetJob is still pending or in_progress (UI or raw API code). */
export function welcomeWidgetJobIsProcessing(rawStatus) {
  if (rawStatus === "loading") return true;
  const s = rawStatus;
  return s === 1 || s === 2 || s === "pending" || s === "in_progress";
}

function hasNonEmptyText(value) {
  return value != null && String(value).trim() !== "";
}

function datasetsHaveValues(datasets) {
  if (!Array.isArray(datasets)) return false;
  return datasets.some(
    (dataset) =>
      dataset?.data &&
      Array.isArray(dataset.data) &&
      dataset.data.length > 0,
  );
}

function itemsHaveValues(items) {
  if (!Array.isArray(items) || items.length === 0) return false;
  return items.some((item) => {
    if (!item || typeof item !== "object") return false;
    return Object.values(item).some((val) => hasNonEmptyText(val));
  });
}

function narrativeHasContent(narrative) {
  if (!narrative || typeof narrative !== "object") return false;
  if (hasNonEmptyText(narrative.summary)) return true;
  if (hasNonEmptyText(narrative.headline)) return true;
  const bullets = narrative.bullets;
  return (
    Array.isArray(bullets) &&
    bullets.some((bullet) => hasNonEmptyText(bullet))
  );
}

function hottermsNgramsHaveContent(ngrams) {
  if (!ngrams || typeof ngrams !== "object") return false;
  return Object.keys(ngrams).some((k) => {
    const row = ngrams[k];
    return Array.isArray(row) && row.length > 0;
  });
}

function summaryTabsHaveContent(tabs) {
  if (!Array.isArray(tabs) || tabs.length === 0) return false;
  return tabs.some((tab) => {
    if (!tab || typeof tab !== "object") return false;
    if (tab.payload_pending || tab.content_pending) return true;
    if (hasNonEmptyText(tab.content)) return true;
    if (Array.isArray(tab.decisions) && tab.decisions.length > 0) return true;
    if (datasetsHaveValues(tab.datasets)) return true;
    if (hottermsNgramsHaveContent(tab.ngrams)) return true;
    return false;
  });
}

function brandKpiHeroHeaderHasDisplayableContent(data) {
  if (!data || typeof data !== "object") return false;
  const headerVal = Number(data.header?.value);
  return Number.isFinite(headerVal);
}

function chartPayloadHasDisplayableContent(payload) {
  if (!payload || typeof payload !== "object") return false;
  if (typeof payload.error === "string" && payload.error.trim()) return false;
  if (datasetsHaveValues(payload.datasets)) return true;
  if (itemsHaveValues(payload.items)) return true;
  const chartType = payload.chart_type;
  if (chartType === "review_statistics") {
    const headerVal = Number(payload.header?.value);
    if (Number.isFinite(headerVal) && headerVal > 0) return true;
    if (
      Array.isArray(payload.time_series?.labels) &&
      payload.time_series.labels.length > 0
    ) {
      return true;
    }
    return datasetsHaveValues(payload.distribution?.datasets);
  }
  if (chartType === "doughnut") {
    const headerVal = Number(payload.header?.value);
    if (Number.isFinite(headerVal) && headerVal > 0) return true;
    return datasetsHaveValues(payload.datasets);
  }
  if (chartType === "bar") {
    const headerVal = Number(payload.header?.value);
    if (Number.isFinite(headerVal)) return true;
    if (Array.isArray(payload.labels) && payload.labels.length > 0) return true;
    return false;
  }
  if (
    chartType === "stacked_bar" &&
    Array.isArray(payload.labels) &&
    payload.labels.length > 0 &&
    datasetsHaveValues(payload.datasets)
  ) {
    return true;
  }
  if (chartType === "topic_doughnut_grid" && Array.isArray(payload.topics)) {
    return payload.topics.some((topic) => datasetsHaveValues(topic?.datasets));
  }
  return false;
}

function topicTabsHaveContent(tabs) {
  if (!Array.isArray(tabs) || tabs.length === 0) return false;
  return tabs.some((tab) => {
    if (!tab || typeof tab !== "object") return false;
    if (tab.payload_pending) return true;
    if (chartPayloadHasDisplayableContent(tab.payload)) return true;
    if (datasetsHaveValues(tab.datasets)) return true;
    if (Array.isArray(tab.items) && tab.items.length > 0) return true;
    return hasNonEmptyText(tab.content);
  });
}

function parseWelcomeWidgetReviewCount(raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.round(n));
}

/** Scoped review count from KPI payload and/or widget row (charts, GenAI, KPI hero). */
export function resolveWelcomeWidgetScopedReviewCount(data, widgetRow) {
  const payload = data && typeof data === "object" ? data : {};
  const fromInsufficient = parseWelcomeWidgetReviewCount(
    payload.insufficient_reviews_count,
  );
  if (fromInsufficient != null) {
    return fromInsufficient;
  }

  const scope = resolveWelcomeReviewScopeFromData(payload, widgetRow);
  if (scope.scopeTotal != null) {
    return scope.scopeTotal;
  }
  if (scope.analyzed != null) {
    return scope.analyzed;
  }

  for (const key of ["source_review_count", "review_count", "num_of_reviews"]) {
    const parsed = parseWelcomeWidgetReviewCount(payload[key]);
    if (parsed != null) {
      return parsed;
    }
  }

  const mid = Number(widgetRow?.metric_id);
  if (mid === 27 && Array.isArray(payload.summary_tabs)) {
    const general = payload.summary_tabs.find(
      (tab) => tab && String(tab.id || "").toLowerCase() === "general",
    );
    const tabCount = parseWelcomeWidgetReviewCount(
      general?.review_count ?? general?.reviewCount,
    );
    if (tabCount != null) {
      return tabCount;
    }
  }

  const header = payload.header;
  if (header && typeof header === "object") {
    const headerCount = parseWelcomeWidgetReviewCount(
      header.review_count ?? header.num_of_reviews,
    );
    if (headerCount != null) {
      return headerCount;
    }
    if (payload.chart_type === "review_statistics") {
      const headerVal = parseWelcomeWidgetReviewCount(header.value);
      if (headerVal != null) {
        return headerVal;
      }
    }
  }

  const rowTotal = parseWelcomeWidgetReviewCount(widgetRow?.review_count_total);
  if (rowTotal != null) {
    return rowTotal;
  }

  return null;
}

export function welcomeWidgetPayloadIsInsufficientReviews(data) {
  return Boolean(data && data.insufficient_reviews);
}

/**
 * Return { currentCount, requiredCount } when widget should show insufficient UI.
 * Covers explicit API insufficient_reviews and empty KPI with scoped count below threshold.
 */
export function resolveWelcomeInsufficientReviewCounts(
  data,
  metricId,
  widgetRow,
) {
  const payload = data && typeof data === "object" ? data : {};
  const requiredCount = resolveWelcomeWidgetMinReviewCount(metricId);

  if (welcomeWidgetPayloadIsInsufficientReviews(payload)) {
    const currentCount =
      resolveWelcomeWidgetScopedReviewCount(payload, widgetRow) ?? 0;
    let resolvedRequired = requiredCount;
    const rawRequired = payload.insufficient_reviews_min_required;
    const parsedRequired = parseWelcomeWidgetReviewCount(rawRequired);
    if (parsedRequired != null && parsedRequired > 0) {
      resolvedRequired = parsedRequired;
    }
    return { currentCount, requiredCount: resolvedRequired };
  }

  const currentCount = resolveWelcomeWidgetScopedReviewCount(payload, widgetRow);
  if (currentCount == null || currentCount >= requiredCount) {
    return null;
  }

  if (welcomeKpiPayloadHasDisplayableContent(payload)) {
    return null;
  }

  return { currentCount, requiredCount };
}

export function welcomeWidgetShowsInsufficientState(data, metricId, widgetRow) {
  return resolveWelcomeInsufficientReviewCounts(data, metricId, widgetRow) != null;
}

export function welcomeKpiErrorMeansNoData(error) {
  const msg = String(error || "").trim().toLowerCase();
  if (msg.startsWith("no ") && msg.includes("available")) return true;
  if (msg.includes("no topics found")) return true;
  if (msg.startsWith("no ") && msg.includes("data")) return true;
  if (welcomeJobStatusDescriptionMeansEmptyGenaiOutput(msg)) return true;
  return false;
}

/** Worker/API: GenAI ran but produced no displayable summary/highlights (not a system failure). */
export function welcomeJobStatusDescriptionMeansEmptyGenaiOutput(raw) {
  const msg = String(raw || "").trim().toLowerCase();
  if (!msg) return false;
  if (msg.includes("no valid topic summaries produced")) return true;
  if (msg.includes("no valid highlights topic summaries produced")) return true;
  if (msg.includes("empty narrative output")) return true;
  if (msg.includes("empty highlights output")) return true;
  if (msg.includes("empty narrative text")) return true;
  return false;
}

/** Worker marked job failed because review counts were below the analysis threshold. */
export function welcomeJobStatusDescriptionMeansInsufficientData(raw) {
  const msg = String(raw || "").trim().toLowerCase();
  if (!msg) return false;
  if (msg.includes("too few reviews")) return true;
  if (msg.includes("insufficient reviews")) return true;
  if (msg.includes("not enough reviews")) return true;
  return false;
}

/** Failed job should show empty/insufficient content — not a hard load failure. */
export function welcomeWidgetFailedJobShowsContentState(widgetRow) {
  if (widgetRow?.job_empty_genai_signal || widgetRow?.job_insufficient_data_signal) {
    return true;
  }
  if (welcomeJobStatusDescriptionMeansEmptyGenaiOutput(widgetRow?.job_status_description)) {
    return true;
  }
  if (welcomeJobStatusDescriptionMeansInsufficientData(widgetRow?.job_status_description)) {
    return true;
  }
  return false;
}

export function welcomeWidgetJobErrorIsInsufficientData(widgetRow) {
  if (widgetRow?.job_insufficient_data_signal) return true;
  return welcomeJobStatusDescriptionMeansInsufficientData(
    widgetRow?.job_status_description,
  );
}

export function welcomeWidgetJobErrorIsEmptyGenaiSignal(widgetRow) {
  if (widgetRow?.job_empty_genai_signal) return true;
  return welcomeJobStatusDescriptionMeansEmptyGenaiOutput(
    widgetRow?.job_status_description,
  );
}

export function welcomeWidgetShowsEmptyGenaiSignal(data, widgetRow) {
  // Re-queued / running jobs must not keep showing the previous empty-GenAI terminal UI.
  const st = Number(widgetRow?.status);
  if (st === 1 || st === 2) return false;
  const raw = String(widgetRow?.status || "")
    .trim()
    .toLowerCase();
  if (raw === "pending" || raw === "in_progress") return false;
  if (data?.empty_genai_output) return true;
  if (widgetRow?.job_empty_genai_signal) return true;
  if (
    welcomeJobStatusDescriptionMeansEmptyGenaiOutput(
      widgetRow?.job_status_description,
    )
  ) {
    return true;
  }
  if (welcomeGenaiMetricHasEmptyTerminalPayload(data, widgetRow)) {
    return true;
  }
  return false;
}

/** Completed GenAI with enough scoped reviews but no displayable summary/highlights. */
export function welcomeGenaiMetricHasEmptyTerminalPayload(data, widgetRow) {
  if (!data || typeof data !== "object") return false;
  if (data.insufficient_reviews) return false;
  if (welcomeKpiPayloadHasDisplayableContent(data)) return false;
  const metricId = Number(widgetRow?.metric_id ?? data?.metric_id ?? 0);
  if (!isWelcomeGenAiContentMetricWidget({ metric_id: metricId, ...widgetRow })) {
    return false;
  }
  const explicit = data.data_status;
  if (
    explicit === WELCOME_WIDGET_DATA_STATUS_FAILED ||
    (explicit != null &&
      explicit !== WELCOME_WIDGET_DATA_STATUS_EMPTY &&
      explicit !== "ok")
  ) {
    return false;
  }
  const scope = resolveWelcomeWidgetScopedReviewCount(data, widgetRow);
  const minRequired = resolveWelcomeWidgetMinReviewCount(metricId);
  if (scope == null || scope < minRequired) return false;
  return true;
}

/** True when KPI payload has something the Welcome UI can render (mirrors API helper). */
export function welcomeKpiPayloadHasDisplayableContent(data) {
  if (!data || typeof data !== "object") return false;
  if (data.insufficient_reviews) return true;
  if (data.error) return welcomeKpiErrorMeansNoData(data.error);

  if (brandKpiHeroHeaderHasDisplayableContent(data)) return true;
  if (chartPayloadHasDisplayableContent(data)) return true;
  if (datasetsHaveValues(data.datasets)) return true;
  if (itemsHaveValues(data.items)) return true;
  if (narrativeHasContent(data.narrative)) return true;
  if (summaryTabsHaveContent(data.summary_tabs)) return true;
  if (topicTabsHaveContent(data.topic_tabs)) return true;

  if (data.chart_type === "hotterms" && hottermsNgramsHaveContent(data.ngrams)) {
    return true;
  }

  const decisions = data.decisions;
  if (Array.isArray(decisions) && decisions.length > 0) return true;

  const competitive = data.data;
  if (competitive && typeof competitive === "object") {
    const topics = competitive.topics;
    if (Array.isArray(topics) && topics.length > 0) {
      const hasTopicData = topics.some((topic) =>
        datasetsHaveValues(topic?.data?.datasets),
      );
      if (hasTopicData) return true;
    }
  }

  return false;
}

/**
 * Resolve widget KPI display state from API payload and optional TanStack query error.
 * Returns "ok" | "empty" | "failed".
 */
export function resolveWelcomeWidgetDataState(
  data,
  { queryFailed = false, metricId, widgetRow } = {},
) {
  if (queryFailed) return WELCOME_WIDGET_DATA_STATUS_FAILED;
  if (!data || typeof data !== "object") {
    if (
      welcomeWidgetShowsInsufficientState({}, metricId, widgetRow)
    ) {
      return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
    }
    return WELCOME_WIDGET_DATA_STATUS_FAILED;
  }

  const explicit = data.data_status;
  if (explicit === WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT) {
    return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
  }
  if (
    explicit === WELCOME_WIDGET_DATA_STATUS_FAILED ||
    explicit === WELCOME_WIDGET_DATA_STATUS_EMPTY
  ) {
    if (welcomeWidgetShowsInsufficientState(data, metricId, widgetRow)) {
      return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
    }
    return explicit;
  }

  if (data.error) {
    if (welcomeKpiErrorMeansNoData(data.error)) {
      if (welcomeWidgetShowsInsufficientState(data, metricId, widgetRow)) {
        return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
      }
      return WELCOME_WIDGET_DATA_STATUS_EMPTY;
    }
    return WELCOME_WIDGET_DATA_STATUS_FAILED;
  }
  if (welcomeWidgetShowsEmptyGenaiSignal(data, widgetRow)) {
    return WELCOME_WIDGET_DATA_STATUS_EMPTY;
  }
  if (welcomeWidgetShowsInsufficientState(data, metricId, widgetRow)) {
    return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
  }
  if (welcomeKpiPayloadHasDisplayableContent(data)) return "ok";

  if (Object.keys(data).length === 0) {
    if (welcomeWidgetShowsInsufficientState(data, metricId, widgetRow)) {
      return WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT;
    }
    return WELCOME_WIDGET_DATA_STATUS_EMPTY;
  }

  return WELCOME_WIDGET_DATA_STATUS_EMPTY;
}

export function WelcomeWidgetGenaiPendingState({
  t,
  className,
  widget = null,
  progressClassNames = null,
}) {
  return (
    <div className={className} aria-busy="true">
      <Spin size="large" />
      {widget && progressClassNames ? (
        <WelcomeWidgetJobProgressView
          widget={widget}
          t={t}
          variant="panel"
          className={progressClassNames.panelWrap}
          trackClassName={progressClassNames.panelTrack}
          barClassName={progressClassNames.panelBar}
          metaClassName={progressClassNames.panelMeta}
          indeterminateClassName={progressClassNames.indeterminate}
        />
      ) : null}
      <p>{t("welcome.widgetGenaiPendingMessage")}</p>
    </div>
  );
}

export function WelcomeWidgetInsufficientOrEmpty({
  data,
  metricId,
  widgetRow,
  t,
  className,
  insufficientClassName,
}) {
  const counts = resolveWelcomeInsufficientReviewCounts(
    data,
    metricId ?? widgetRow?.metric_id,
    widgetRow,
  );
  if (counts) {
    return (
      <InsufficientDataState
        currentCount={counts.currentCount}
        requiredCount={counts.requiredCount}
        className={insufficientClassName}
      />
    );
  }
  if (welcomeWidgetShowsEmptyGenaiSignal(data, widgetRow)) {
    return (
      <WelcomeWidgetDataStateMessage
        status={WELCOME_WIDGET_DATA_STATUS_EMPTY}
        t={t}
        emptyClassName={className}
        data={data}
        widgetRow={widgetRow}
        emptyGenaiSignal
      />
    );
  }
  return (
    <WelcomeWidgetDataStateMessage
      status={WELCOME_WIDGET_DATA_STATUS_EMPTY}
      t={t}
      emptyClassName={className}
      data={data}
      widgetRow={widgetRow}
    />
  );
}

export function WelcomeWidgetDataStateMessage({
  status,
  t,
  onRefresh,
  refreshing = false,
  onRegenerate,
  regenerating = false,
  className,
  failedClassName,
  failedActionsClassName,
  emptyClassName,
  pendingClassName,
  insufficientCounts,
  insufficientClassName,
  data = null,
  widgetRow = null,
  emptyGenaiSignal = false,
}) {
  if (status === WELCOME_WIDGET_DATA_STATUS_INSUFFICIENT && insufficientCounts) {
    return (
      <InsufficientDataState
        currentCount={insufficientCounts.currentCount}
        requiredCount={insufficientCounts.requiredCount}
        className={insufficientClassName}
      />
    );
  }

  if (status === WELCOME_WIDGET_DATA_STATUS_PENDING) {
    return (
      <WelcomeWidgetGenaiPendingState
        t={t}
        className={pendingClassName || className}
      />
    );
  }

  const failed = status === WELCOME_WIDGET_DATA_STATUS_FAILED;
  const rootClass = failed
    ? failedClassName || className
    : emptyClassName || className;

  if (failed) {
    const showReload = typeof onRefresh === "function";
    const showRegenerate = typeof onRegenerate === "function";
    return (
      <div className={rootClass}>
        <p>{t("welcome.widgetDataLoadFailed")}</p>
        {showReload || showRegenerate ? (
          <div
            className={failedActionsClassName}
            style={
              failedActionsClassName
                ? undefined
                : {
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    justifyContent: "center",
                  }
            }
          >
            {showReload ? (
              <Button
                type="default"
                size="small"
                icon={<ReloadOutlined />}
                loading={refreshing}
                disabled={regenerating}
                onClick={onRefresh}
              >
                {t("welcome.reloadWidgetData")}
              </Button>
            ) : null}
            {showRegenerate ? (
              <Button
                type="primary"
                size="small"
                icon={<ThunderboltOutlined />}
                loading={regenerating}
                disabled={refreshing}
                onClick={onRegenerate}
              >
                {t("welcome.regenerate")}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  const scopeCount =
    resolveWelcomeWidgetScopedReviewCount(data, widgetRow) ??
    parseWelcomeWidgetReviewCount(data?.source_review_count) ??
    parseWelcomeWidgetReviewCount(data?.scope_review_count_total);
  const errMsg = String(data?.error ?? "").toLowerCase();
  if (
    scopeCount != null &&
    scopeCount > 0 &&
    errMsg.includes("sentiment")
  ) {
    return (
      <div className={rootClass}>
        {t("welcome.widgetEmptySentimentUnlabeled", { count: scopeCount })}
      </div>
    );
  }

  if (emptyGenaiSignal || welcomeWidgetShowsEmptyGenaiSignal(data, widgetRow)) {
    const requiredCount = resolveWelcomeWidgetMinReviewCount(
      widgetRow?.metric_id ?? data?.metric_id ?? data?.welcome_metric_id,
    );
    const reason = String(widgetRow?.job_status_description || "").trim();
    const publicReason = reason
      ? formatWelcomeEmptyGenaiPublicReason(reason, t)
      : "";
    return (
      <div className={rootClass}>
        <div style={{ maxWidth: 520, margin: "0 auto", padding: "4px 8px" }}>
          <p style={{ margin: 0 }}>{t("welcome.widgetEmptyGenaiNoSignal")}</p>
          {scopeCount != null ? (
            <p style={{ margin: "8px 0 0", opacity: 0.88, fontSize: "13px" }}>
              {t("welcome.widgetEmptyGenaiNoSignalDetail", {
                count: scopeCount,
                required: requiredCount,
              })}
            </p>
          ) : null}
          {publicReason ? (
            <p
              style={{
                margin: "6px 0 0",
                opacity: 0.72,
                fontSize: "12px",
                lineHeight: 1.45,
              }}
            >
              {t("welcome.widgetEmptyGenaiNoSignalReason", {
                reason: publicReason,
              })}
            </p>
          ) : null}
        </div>
      </div>
    );
  }

  return <div className={rootClass}>{t("welcome.widgetEmptyDataCount")}</div>;
}

/** Map common worker English status strings to short localized reasons. */
function formatWelcomeEmptyGenaiPublicReason(raw, t) {
  const msg = String(raw || "").trim();
  if (!msg) return "";
  const lower = msg.toLowerCase();
  if (lower.includes("empty genai output")) {
    return t("welcome.widgetEmptyGenaiReasonEmptyNarrative");
  }
  if (lower.includes("no valid topic summaries")) {
    return t("welcome.widgetEmptyGenaiReasonNoTopicSummaries");
  }
  if (lower.includes("empty narrative")) {
    return t("welcome.widgetEmptyGenaiReasonEmptyNarrative");
  }
  if (lower.includes("no valid highlights")) {
    return t("welcome.widgetEmptyGenaiReasonNoHighlights");
  }
  if (lower.includes("too few reviews per topic")) {
    return t("welcome.widgetEmptyGenaiReasonTooFewPerTopic");
  }
  return msg.length > 280 ? `${msg.slice(0, 277)}…` : msg;
}
